import { Button, ButtonGroup, InputGroup, Form } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { SourceFilesListProps } from "./SourceFilesList";
import SourceFilesListSummary from "./SourceFilesListSummary";

/** Handle the logic for sorting the files list and deciding which radio button is checked. */
function useHandleSortingFilesList(props: SourceFilesListProps) {
    const { sortedByName, isSortedByName, sortedByDate, isSortedByDate } = useMemo(() => {
        const sortedByName = [...props.sourceFiles].sort((a, b) =>
            a.file.name.localeCompare(b.file.name, undefined, { numeric: true, sensitivity: "base" }),
        );

        const allHaveStart = props.sourceFiles.every((f) => f.start !== undefined);
        const sortedByDate = allHaveStart
            ? [...props.sourceFiles].sort((a, b) => {
                  return a.start.value.valueOf() - b.start.value.valueOf();
              })
            : undefined;

        return {
            sortedByName,
            isSortedByName: props.sourceFiles.every((a, i) => a.file.name == sortedByName[i].file.name),
            sortedByDate,
            isSortedByDate:
                sortedByDate !== undefined &&
                props.sourceFiles.every((a, i) => a.file.name == sortedByDate[i].file.name),
        };
    }, [props.sourceFiles]);

    const defaultChecked = isSortedByName ? "name" : isSortedByDate ? "date" : null;
    // Value used to alternate between checking the date or name radios, even when sorting by either
    // leads to the same result
    const [sortOption, setSortOption] = useState<"name" | "date" | null>(defaultChecked);

    useEffect(() => {
        // Skip reset if checked value is valid
        if ((isSortedByDate && sortOption == "date") || (isSortedByName && sortOption == "name")) {
            return;
        }
        setSortOption(defaultChecked);
    }, [props.sourceFiles]);

    function sortByName() {
        setSortOption("name");
        props.resetSelectedFiles();

        if (!isSortedByName) {
            props.setSourceFiles(sortedByName);
        }
    }

    function sortByDate() {
        if (sortedByDate === undefined) {
            return;
        }

        setSortOption("date");
        props.resetSelectedFiles();

        if (!isSortedByDate) {
            props.setSourceFiles(sortedByDate);
        }
    }

    return { checked: sortOption, sortByName, sortByDate };
}

function SourceFilesListControls(props: SourceFilesListProps) {
    const { checked, sortByName, sortByDate } = useHandleSortingFilesList(props);

    function clear() {
        props.resetSelectedFiles();
        props.resetSourceFiles();
    }

    return (
        <div className="file-list-controls">
            <InputGroup style={{ alignItems: "center" }} size="sm">
                <InputGroup.Text>Sort By</InputGroup.Text>
                <Button
                    title="Sort the source files alphabetically by their name"
                    onClick={sortByName}
                    variant={`${checked == "name" ? "" : "outline-"}secondary`}
                >
                    Name
                </Button>
                <Button
                    title="Sort the source files by each file's last modification date"
                    onClick={sortByDate}
                    variant={`${checked == "date" ? "" : "outline-"}secondary`}
                >
                    Date Modified
                </Button>
            </InputGroup>

            <SourceFilesListSummary {...props} />

            <Button
                style={{ justifySelf: "end" }}
                size="sm"
                variant="outline-danger"
                onClick={clear}
                title="Clear files list"
            >
                Clear
            </Button>
        </div>
    );
}

export default SourceFilesListControls;
